/**
 * Totem Cards Configuration
 *
 * TOTEM_CARDS_CONFIG defines the STRUCTURE only.
 * All text content comes from translation files via i18n in templates.
 * Each block has a `slug` (totem color) that maps to `totemSection.{slug}.*` in translations.
 */

import { CARD_GRADIENTS } from '@/config/colors.config'
import { NEWS_GALLERY_CONFIG } from '@/config/news.config'
import type { TotemCardItem } from '@/types'
import { carouselImg } from '@/utils/index'

export const TOTEM_CARDS_CONFIG: TotemCardItem[] = [
  // ============================================
  // BLUE - INVEST
  // ============================================
  {
    id: 1,
    slug: 'blue',
    backgroundGradient: CARD_GRADIENTS.blue,
    textColor: '#1a1a1a',
    content: [
      { type: 'spacer', height: '5rem' },
      { type: 'paragraph' },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote' },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-1-caption',
        images: [
          carouselImg('blue/blue_c1_s1.jpg', 'Event photo 1'),
          carouselImg('blue/blue_c1_s2.jpg', 'Event photo 2'),
          carouselImg('blue/blue_c1_s3.jpg', 'Event photo 3'),
          carouselImg('blue/blue_c1_s4.jpg', 'Event photo 4'),
          carouselImg('blue/blue_c1_s5.jpg', 'Event photo 5')
        ],
        autoplay: true
      },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote-2' },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-2-caption',
        images: [
          carouselImg('blue/blue_c2_s1.jpg', 'Event photo 1'),
          carouselImg('blue/blue_c2_s2.jpg', 'Event photo 2'),
          carouselImg('blue/blue_c2_s3.jpg', 'Event photo 3'),
          carouselImg('blue/blue_c2_s4.jpg', 'Event photo 4'),
          carouselImg('blue/blue_c2_s5.jpg', 'Event photo 5')
        ],
        autoplay: true
      },
      { type: 'spacer', height: '5rem' },
      { type: 'news', news: NEWS_GALLERY_CONFIG.items.filter(item => item.tag === 'invest'), theme: 'light' },
      { type: 'spacer', height: '10rem' }
    ]
  },
  // ============================================
  // BLACK - BUILD
  // ============================================
  {
    id: 2,
    slug: 'black',
    backgroundGradient: CARD_GRADIENTS.black,
    textColor: '#ffffff',
    content: [
      { type: 'spacer', height: '5rem' },
      { type: 'paragraph' },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote' },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-1-caption',
        images: [
          carouselImg('black/black_c1_s1.jpg', 'Event photo 1'),
          carouselImg('black/black_c1_s2.jpg', 'Event photo 2'),
          carouselImg('black/black_c1_s3.jpg', 'Event photo 3'),
          carouselImg('black/black_c1_s4.jpg', 'Event photo 4'),
          carouselImg('black/black_c1_s5.jpg', 'Event photo 5'),
          carouselImg('black/black_c1_s6.jpg', 'Event photo 6'),
          carouselImg('black/black_c1_s7.jpg', 'Event photo 7')
        ],
        autoplay: true
      },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-2-caption',
        images: [
          carouselImg('black/black_c2_s1.jpg', 'Event photo 1'),
          carouselImg('black/black_c2_s2.jpg', 'Event photo 2'),
          carouselImg('black/black_c2_s3.jpg', 'Event photo 3'),
          carouselImg('black/black_c2_s4.jpg', 'Event photo 4'),
          carouselImg('black/black_c2_s5.jpg', 'Event photo 5'),
          carouselImg('black/black_c2_s6.jpg', 'Event photo 6'),
          carouselImg('black/black_c2_s7.jpg', 'Event photo 7'),
          carouselImg('black/black_c2_s8.jpg', 'Event photo 8'),
          carouselImg('black/black_c2_s9.jpg', 'Event photo 9'),
          carouselImg('black/black_c2_s10.jpg', 'Event photo 10')
        ],
        autoplay: true
      },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote-2' },
      { type: 'spacer', height: '5rem' },
      { type: 'news', news: NEWS_GALLERY_CONFIG.items.filter(item => item.tag === 'build'), theme: 'dark' },
      { type: 'spacer', height: '10rem' }
    ]
  },
  // ============================================
  // RED - SUPPORT
  // ============================================
  {
    id: 3,
    slug: 'red',
    backgroundGradient: CARD_GRADIENTS.red,
    textColor: '#1a1a1a',
    content: [
      { type: 'spacer', height: '5rem' },
      { type: 'paragraph' },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote' },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-1-caption',
        images: [
          carouselImg('red/red_c1_s1.jpg', 'Event photo 1'),
          carouselImg('red/red_c1_s2.jpg', 'Event photo 2'),
          carouselImg('red/red_c1_s3.jpg', 'Event photo 3'),
          carouselImg('red/red_c1_s4.jpg', 'Event photo 4'),
          carouselImg('red/red_c1_s5.jpg', 'Event photo 5'),
          carouselImg('red/red_c1_s6.jpg', 'Event photo 6'),
          carouselImg('red/red_c1_s7.jpg', 'Event photo 7')
        ],
        autoplay: true
      },
      { type: 'spacer', height: '5rem' },
      { type: 'video', captionKey: 'video-1-caption', video: { en: 'https://youtube.com/shorts/bndsOXPleBk', it: 'https://youtube.com/shorts/PQCeK66zjeY' } },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-2-caption',
        images: [
          carouselImg('red/red_c2_s1.jpg', 'Event photo 1'),
          carouselImg('red/red_c2_s2.jpg', 'Event photo 2'),
          carouselImg('red/red_c2_s3.jpg', 'Event photo 3'),
          carouselImg('red/red_c2_s4.jpg', 'Event photo 4'),
          carouselImg('red/red_c2_s5.jpg', 'Event photo 5'),
          carouselImg('red/red_c2_s6.jpg', 'Event photo 6'),
          carouselImg('red/red_c2_s7.jpg', 'Event photo 7'),
          carouselImg('red/red_c2_s8.jpg', 'Event photo 8'),
          carouselImg('red/red_c2_s9.jpg', 'Event photo 9')
        ],
        autoplay: true
      },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote-2' },
      { type: 'spacer', height: '5rem' },
      { type: 'news', news: NEWS_GALLERY_CONFIG.items.filter(item => item.tag === 'support'), theme: 'light' },
      { type: 'spacer', height: '10rem' }
    ]
  },
  // ============================================
  // WHITE - IMPACT
  // ============================================
  {
    id: 4,
    slug: 'white',
    backgroundGradient: CARD_GRADIENTS.white,
    textColor: '#000000',
    content: [
      { type: 'spacer', height: '5rem' },
      { type: 'paragraph' },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote' },
      { type: 'spacer', height: '5rem' },
      { type: 'video', captionKey: 'video-1-caption', video: { en: 'https://youtube.com/shorts/nVCKYRv-IUY', it: 'https://youtube.com/shorts/SRMovshEq1w' } },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-1-caption',
        images: [
          carouselImg('white/white_c1_s1.jpg', 'Event photo 1'),
          carouselImg('white/white_c1_s2.jpg', 'Event photo 2'),
          carouselImg('white/white_c1_s3.jpg', 'Event photo 3'),
          carouselImg('white/white_c1_s4.jpg', 'Event photo 4'),
          carouselImg('white/white_c1_s5.jpg', 'Event photo 5'),
          carouselImg('white/white_c1_s6.jpg', 'Event photo 6')
        ],
        autoplay: true
      },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-2-caption',
        images: [
          carouselImg('white/white_c2_s1.jpg', 'Event photo 1'),
          carouselImg('white/white_c2_s2.jpg', 'Event photo 2'),
          carouselImg('white/white_c2_s3.jpg', 'Event photo 3'),
          carouselImg('white/white_c2_s4.jpg', 'Event photo 4'),
          carouselImg('white/white_c2_s5.jpg', 'Event photo 5'),
          carouselImg('white/white_c2_s6.jpg', 'Event photo 6'),
          carouselImg('white/white_c2_s7.jpg', 'Event photo 7'),
          carouselImg('white/white_c2_s8.jpg', 'Event photo 8'),
          carouselImg('white/white_c2_s9.jpg', 'Event photo 9')
        ],
        autoplay: true
      },
      { type: 'spacer', height: '5rem' },
      { type: 'video', captionKey: 'video-2-caption', video: { en: 'https://youtube.com/shorts/SiQxXM_Gk5U', it: 'https://youtube.com/shorts/Zlf2PymgA_k' } },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote-2' },
      { type: 'spacer', height: '5rem' },
      { type: 'news', news: NEWS_GALLERY_CONFIG.items.filter(item => item.tag === 'impact'), theme: 'light' },
      { type: 'spacer', height: '10rem' }
    ]
  },
  // ============================================
  // YELLOW - GROW
  // ============================================
  {
    id: 5,
    slug: 'yellow',
    backgroundGradient: CARD_GRADIENTS.yellow,
    textColor: '#1a1a1a',
    content: [
      { type: 'spacer', height: '5rem' },
      { type: 'paragraph' },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote' },
      { type: 'spacer', height: '5rem' },
      { type: 'video', captionKey: 'video-1-caption', video: { en: 'https://youtube.com/shorts/cLhKCOlP6QE', it: 'https://youtube.com/shorts/m_vlaAY-mEY' } },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-1-caption',
        images: [
          carouselImg('yellow/yellow_c1_s1.jpg', 'Event photo 1'),
          carouselImg('yellow/yellow_c1_s2.jpg', 'Event photo 2'),
          carouselImg('yellow/yellow_c1_s3.jpg', 'Event photo 3'),
          carouselImg('yellow/yellow_c1_s4.jpg', 'Event photo 4'),
          carouselImg('yellow/yellow_c1_s5.jpg', 'Event photo 5'),
          carouselImg('yellow/yellow_c1_s6.jpg', 'Event photo 6')
        ],
        autoplay: false
      },
      { type: 'spacer', height: '5rem' },
      { type: 'video', captionKey: 'video-2-caption', video: { en: 'https://youtube.com/shorts/sQVMecvjDgs', it: 'https://youtube.com/shorts/KFSdcR8Iem4' } },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote-2' },
      { type: 'spacer', height: '5rem' },
      { type: 'news', news: NEWS_GALLERY_CONFIG.items.filter(item => item.tag === 'grow'), theme: 'light' },
      { type: 'spacer', height: '10rem' }
    ]
  },
  // ============================================
  // GREEN - WIN
  // ============================================
  {
    id: 6,
    slug: 'green',
    backgroundGradient: CARD_GRADIENTS.green,
    textColor: '#1a1a1a',
    content: [
      { type: 'spacer', height: '5rem' },
      { type: 'paragraph' },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote' },
      { type: 'spacer', height: '5rem' },
      { type: 'video', captionKey: 'video-1-caption', video: { en: 'https://youtube.com/shorts/Q4o2Jj48aiM', it: 'https://youtube.com/shorts/GrLtBkmb06U' } },
      { type: 'spacer', height: '5rem' },
      {
        type: 'carousel',
        captionKey: 'carousel-1-caption',
        images: [
          carouselImg('green/green_c1_s1.jpg', 'Event photo 1'),
          carouselImg('green/green_c1_s2.jpg', 'Event photo 2'),
          carouselImg('green/green_c1_s3.jpg', 'Event photo 3'),
          carouselImg('green/green_c1_s4.jpg', 'Event photo 4'),
          carouselImg('green/green_c1_s5.jpg', 'Event photo 5'),
          carouselImg('green/green_c1_s6.jpg', 'Event photo 6'),
          carouselImg('green/green_c1_s7.jpg', 'Event photo 7'),
          carouselImg('green/green_c1_s8.jpg', 'Event photo 8')
        ],
        autoplay: true
      },
      { type: 'spacer', height: '5rem' },
      { type: 'quote', quoteKey: 'quote-2' },
      { type: 'spacer', height: '5rem' },
      { type: 'video', captionKey: 'video-2-caption', video: { en: 'https://youtube.com/shorts/2em_c0nq-0g', it: 'https://youtube.com/shorts/pBeRRS6k6pI' } },
      { type: 'spacer', height: '5rem' },
      { type: 'news', news: NEWS_GALLERY_CONFIG.items.filter(item => item.tag === 'win'), theme: 'light' },
      { type: 'spacer', height: '10rem' }
    ]
  }
]
